import { createStore } from "@escapio/shared/data-store/DataStore";
import {
	fetchPrices,
	fetchPricesForCurrentTravelData,
} from "@escapio/portal/api/hotel-prices";
import { data } from "@escapio/shared/state/payload";
import { trackViewContent } from "@escapio/portal/tracker/Facebook";
import { trackNoPrices } from "./tracker";

import { format, parse } from "./FilterToHashMapper";
import { formatRequestParameter, travelData } from "../../state/travelData";
import * as Board from "@escapio/portal/components/board";
import { replaceState } from "@escapio/shared/state/urlHash";
import { hasDirectLink } from "@escapio/portal/state/DirectLink";
import {
	LOADING_STATE,
	NO_PRICES_STATE,
} from "@escapio/portal/api/prices-state";
import { hasEscapioRate } from "@escapio/portal/state/escapio-deals-helpers";

export const STATE_LOADING = "loading";
export const STATE_AVAILABLE = "available";
export const STATE_UNAVAILABLE = "unavailable";
export const STATE_ALTERNATIVES = "alternatives";
export const STATE_WITHOUT_DATES = "without-dates";

const hotel = data.hotel;
const HAS_DIRECT_LINK = hasDirectLink(hotel);

const FILTER_BOARD = "board";
const FILTER_CANCELATION = "cancelation";

const DEFAULT_FILTERS = Object.freeze({
	[FILTER_BOARD]: Board.NONE,
	[FILTER_CANCELATION]: false,
});

const HAS_NO_PRICE_STATES = [STATE_UNAVAILABLE, STATE_ALTERNATIVES];

export const ACTION_PARSE_HASH = (state, payload, dispatch) => {
	const { selectedStagePicture, ...filters } = parse(payload);
	const newState =
		selectedStagePicture !== undefined
			? { ...state, selectedStagePicture }
			: state;
	return ACTION_SET_FILTER(newState, filters, dispatch);
};

export const ACTION_REQUEST_PRICES = (state, _, dispatch) => {
	const newState = {
		...state,
		hasActiveEscapioRates: hasEscapioRate(hotel),
	};

	if (!travelData.hasDates) {
		return {
			...newState,
			status: STATE_WITHOUT_DATES,
			prices: NO_PRICES_STATE,
		};
	}

	const filters = Object.entries(state.filters)
		.filter(([, enabled]) => enabled)
		.reduce((acc, [name, value]) => ({ ...acc, [name]: value }), {});

	fetchPricesForCurrentTravelData([hotel.id], filters).then(
		response => {
			if (!response[hotel.id].hasOffers && !hasEscapioRate(hotel)) {
				dispatch(ACTION_REQUEST_ALTERNATIVES, {
					filters,
				});
			} else {
				dispatch(ACTION_SET_PRICES, response[hotel.id]);
			}
		},
		() => {
			dispatch(ACTION_REQUEST_ALTERNATIVES, { filters });
		},
	);

	return {
		...newState,
		status: STATE_LOADING,
		prices: LOADING_STATE,
	};
};

const ACTION_REQUEST_ALTERNATIVES = (state, { filters }, dispatch) => {
	const dateShift = [1, -1, 7, 14];
	Promise.all(
		dateShift
			.map(shift => {
				const arrival = new Date(travelData.arrival.valueOf());
				const departure = new Date(travelData.departure.valueOf());
				arrival.setDate(arrival.getDate() + shift);
				departure.setDate(departure.getDate() + shift);
				return [arrival, departure];
			})
			.filter(([arrival]) => arrival >= new Date(Date.now()))
			.map(([arrival, departure]) => {
				return fetchPrices(
					[hotel.id],
					formatRequestParameter(
						arrival,
						departure,
						travelData.rooms,
						travelData.currency,
					),
					filters,
				).then(
					response => {
						if (response[hotel.id]?.hasOffers) {
							dispatch(ACTION_ADD_ALTERNATIVE, {
								arrival,
								departure,
								bestPrice: response[hotel.id].bestPrice,
							});
						}
					},
					() => {
						// Ignore.
					},
				);
			}),
	).finally(() => {
		dispatch(ACTION_FINISH_LOADING);
	});

	return {
		...state,
		prices: NO_PRICES_STATE,
		status: STATE_LOADING,
		alternatives: [],
	};
};

const ACTION_SET_PRICES = (state, prices, dispatch) =>
	ACTION_SET_STATUS(
		{ ...state, prices },
		prices.hasOffers ? STATE_AVAILABLE : STATE_UNAVAILABLE,
		dispatch,
	);

const ACTION_ADD_ALTERNATIVE = (state, alternative) => {
	const byDate = (a, b) => (a.arrival < b.arrival ? -1 : 1);
	const alternatives = [...state.alternatives, alternative];

	return {
		...state,
		alternatives: alternatives.sort(byDate),
	};
};

const ACTION_FINISH_LOADING = state => {
	if (state.alternatives && state.alternatives.length > 0) {
		return ACTION_SET_STATUS(state, STATE_ALTERNATIVES);
	}

	return ACTION_SET_STATUS(state, STATE_UNAVAILABLE);
};

const getNoPriceAction = status => {
	switch (status) {
		case STATE_UNAVAILABLE:
			return HAS_DIRECT_LINK ? "Profil nur EDL" : "Profil leer";
		case STATE_ALTERNATIVES:
			return HAS_DIRECT_LINK
				? "Profil EDL & Alternativen"
				: "Profil nur Alternativen";
	}
};

const ACTION_SET_STATUS = (state, status) => {
	if (status === STATE_AVAILABLE && hasEscapioRate(hotel)) {
		state.prices.offers.forEach(offer => {
			trackViewContent({
				hotel: { id: hotel.id },
				rate_id: offer.rate.id,
				date_arrival: travelData.arrival.toISOString(),
				date_departure: travelData.departure.toISOString(),
				currency: offer.currency,
				price: offer.total_rate,
			});
		});
	}

	if (HAS_NO_PRICE_STATES.includes(status)) {
		trackNoPrices(getNoPriceAction(status), hotel, state.filters);
	}

	return { ...state, status };
};

export const ACTION_SET_FILTER = (state, filters, dispatch) => {
	const newFilters = { ...state.filters, ...filters };
	const hash = format(newFilters);
	if (hash !== location.hash) {
		replaceState(hash);
	}

	return ACTION_REQUEST_PRICES(
		{ ...state, filters: newFilters },
		null,
		dispatch,
	);
};

export const store = createStore(
	{
		status: STATE_LOADING,
		prices: LOADING_STATE,
		filters: { ...DEFAULT_FILTERS },
		alternatives: null,
		selectedStagePicture: 0,
		hasActiveEscapioRates: hasEscapioRate(hotel),
	},
	{
		boardFilterAvailable:
			({ filters, status }) =>
			value =>
				status === STATE_AVAILABLE ||
				(HAS_NO_PRICE_STATES.includes(status) &&
					Board.isAtLeast(value, filters.board)),
		filterAvailable:
			({ filters, status }) =>
			filter =>
				status === STATE_AVAILABLE ||
				(HAS_NO_PRICE_STATES.includes(status) && filters[filter]),
	},
);
